import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query"
import { routes, type AutomaticValueRecoveryStatus } from "@nova-ai/shared"
import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"
import {
  automaticValueRecoveryOptions,
  useAutomaticValueRecovery,
} from "./use-automatic-value-recovery"

type ControlChange = {
  field: keyof Pick<
    AutomaticValueRecoveryStatus["controls"],
    "replacementEnabled" | "validationEnabled"
  >
  enabled: boolean
}

export function useValueRecoverySettings(marketplace: string, canEdit: boolean) {
  const client = useQueryClient()
  const status = useAutomaticValueRecovery(marketplace)
  const queryKey = queryKeys.automaticValueRecovery(marketplace)
  const mutationKey = [...queryKey, "settings-write"]
  const isSaving = useIsMutating({ mutationKey }) > 0
  const save = useMutation({
    mutationKey,
    retry: false,
    mutationFn: async ({ field, enabled }: ControlChange) => {
      // Read both flags immediately before writing. The API retains last-write semantics.
      await client.cancelQueries({ queryKey })
      const current = await client.fetchQuery(automaticValueRecoveryOptions(marketplace))
      const controls = { ...current.controls, [field]: enabled }
      let saveError: string | null = null
      try {
        const result = await apiFetch<unknown>(
          routes.productValues.recoveryControls({ marketplace }),
          {
            method: "PUT",
            body: {
              replacementEnabled: controls.replacementEnabled,
              validationEnabled: controls.validationEnabled,
              confirm: true,
            },
          }
        )
        if (!result.ok) {
          saveError = result.error.message
        }
      } catch {
        saveError = "Connection interrupted."
      }
      // Fence pre-write reads, including run summaries, before authoritative readback.
      await client.cancelQueries({ queryKey })
      await client.invalidateQueries({ queryKey, refetchType: "none" })
      let confirmed: AutomaticValueRecoveryStatus
      try {
        confirmed = await client.fetchQuery(automaticValueRecoveryOptions(marketplace))
      } catch {
        throw new Error(
          "The save outcome was uncertain. Review the current saved settings before trying again."
        )
      } finally {
        void client.invalidateQueries({
          queryKey,
          predicate: (query) => query.queryKey.length > queryKey.length,
        })
      }
      if (saveError) {
        throw new Error(
          `The save response failed or was uncertain: ${saveError}. Current saved settings are shown.`
        )
      }
      if (confirmed.controls[field] !== enabled) {
        throw new Error(
          "The saved setting differs from your change. Current saved settings are shown; review before trying again."
        )
      }
    },
  })

  const canChange = canEdit && status.isSuccess && !status.isFetching && !isSaving
  return {
    status,
    isSaving,
    canChange,
    save,
    change: (change: ControlChange) => {
      // The cache guard also protects remounts and clicks before React re-renders.
      if (canChange && client.isMutating({ mutationKey }) === 0) {
        save.mutate(change)
      }
    },
    refresh: () => {
      if (client.isMutating({ mutationKey }) > 0) {
        return
      }
      save.reset()
      void status.refetch()
    },
  }
}
